import { DSButton } from "@components/design-system/molecules";
import {
  faForward,
  faThumbsUp,
  faUndo,
} from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import { useWatch } from "react-hook-form";

import WizardStep from "./WizardStep";
import { TEMPLATE_TYPES } from "../constants";
import PreviewNoteDisplay from "../PreviewNoteDisplay";
import {
  TranscriptSampleChipMenu,
  RealNoteDisplay,
  TranscriptPreview,
  PreviewSourceToggle,
  PreviewLoadingState,
} from "../TemplateBuilder/components/ExamplesChipMenu";
import NotePickerModal from "../TemplateBuilder/modals/NotePickerModal";

export default function PreviewStep({
  control,
  onBack,
  onKeepEditing,
  onLooksGood,
  onSkip,
  loadingPreview,
  previewContent,
  handlePreview,
  handleClearSampleTranscript,
  previewSource,
  setPreviewSource,
  selectedNote,
  onSelectNote,
}) {
  const sampleTranscript = useWatch({
    control,
    name: "sampleTranscript",
  });
  const templateType = useWatch({ control, name: "templateType" });
  const isSoap = templateType === TEMPLATE_TYPES.SOAP;
  const [showNotePickerModal, setShowNotePickerModal] =
    useState(false);

  const handleOpenNotePicker = () => {
    setShowNotePickerModal(true);
  };

  const handleSelectNote = (note) => {
    onSelectNote(note);
    setShowNotePickerModal(false);
  };

  return (
    <WizardStep
      title="Preview Your Template"
      description={
        previewContent
          ? "Here's what your template looks like applied to a real case. Happy with it?"
          : "Now for the fun part! Select one of our sample cases or one of your recent notes, then click 'Generate Preview' to see how your template helps generate a note for that case."
      }
      onBack={onBack}
      hideNext
      footerActions={
        previewContent ? (
          <>
            <DSButton
              variant="secondary"
              onClick={onKeepEditing}
              size="lg"
              type="button"
              icon={faUndo}
              dataTestId="wizard-keep-editing-btn"
            >
              Keep Editing
            </DSButton>
            <DSButton
              variant="primary"
              onClick={onLooksGood}
              size="lg"
              type="button"
              icon={faThumbsUp}
              dataTestId="wizard-looks-good-btn"
            >
              Looks Good!
            </DSButton>
          </>
        ) : (
          <>
            <DSButton
              variant="secondary"
              onClick={onSkip}
              size="lg"
              type="button"
              icon={faForward}
              disabled={loadingPreview}
              dataTestId="wizard-skip-preview-btn"
            >
              Skip
            </DSButton>
            <DSButton
              variant="primary"
              size="lg"
              onClick={handlePreview}
              disabled={loadingPreview}
              type="button"
              dataTestId="wizard-step-preview-next-btn"
            >
              {loadingPreview
                ? "Generating..."
                : "Generate Preview →"}
            </DSButton>
          </>
        )
      }
      dataTestId="wizard-step-preview"
    >
      <NotePickerModal
        shouldShow={showNotePickerModal}
        hideModal={() => setShowNotePickerModal(false)}
        onSelectNote={handleSelectNote}
      />

      <div className="flex flex-col gap-md">
        {loadingPreview ? (
          <PreviewLoadingState
            previewSource={previewSource}
            selectedNote={selectedNote}
            sampleTranscript={sampleTranscript}
          />
        ) : previewContent ? (
          <PreviewNoteDisplay
            previewContent={previewContent}
            isSoap={isSoap}
            dataTestId="wizard-preview-note"
          />
        ) : (
          <div className="flex flex-row gap-md">
            {/* Left: source toggle + selector */}
            <div className="flex flex-col gap-md flex-1">
              <PreviewSourceToggle
                previewSource={previewSource}
                setPreviewSource={setPreviewSource}
                testIdPrefix="wizard-preview"
              />

              {previewSource === "sample" ? (
                <TranscriptSampleChipMenu
                  control={control}
                  handleClearSampleTranscript={
                    handleClearSampleTranscript
                  }
                />
              ) : (
                <RealNoteDisplay
                  selectedNote={selectedNote}
                  onOpenNotePicker={handleOpenNotePicker}
                  showTranscript={false}
                />
              )}
            </div>

            {/* Right: transcript display */}
            <TranscriptPreview className="flex-1">
              {previewSource === "note" && selectedNote
                ? selectedNote.transcription
                : sampleTranscript}
            </TranscriptPreview>
          </div>
        )}
      </div>
    </WizardStep>
  );
}
